import React, { useState, useEffect } from 'react';
import { Query } from 'react-apollo';
import { themes } from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';
import { connect } from 'react-redux';
import Loader from 'react-loading';

import { ReduxState } from 'redux';
import useHomeMarket from 'hooks/useHomeMarket';
import { AbsoluteCentered } from '../../styled';
import EmptyState from './EmptyState';
import { TabNavigator } from 'kb-web-shared';
import Assessment from './Assessment';
import Stimulation from './Stimulation';
import EggRetrieval from './EggRetrieval';
import AtHomeTesting from './AtHomeTesting';
import RegisterCustomCode from './AtHomeTesting/RegisterCustomCode';
import { Title } from '../styled_common';
import { RetrievalResult } from './EggRetrieval/types';
import { AtHomeWrapper, ResultsCard, ResultsContainer, ShadowWrapper } from './styled';
import { RESULTS_QUERY } from './graphql/resultsQuery';
import { Props, Tab, TabTypes, ResponseData, DailyResult, ResultsQueryProps } from './types';

const Results = ({ history, patient }: Props) => {
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [currentTab, setCurrentTab] = useState<TabTypes | null>(null);
  const { vios: isViosPatient } = useHomeMarket();

  const tabNavigatorThemeConfig = {
    tab: {
      selectedBG: themes.colors.KBNight,
      deselectedBG: themes.colors.KBTwilightLight,
      selectedTitle: '#fff',
      deselectedTitle: themes.colors.KBGrey,
      fullWidthTabs: true
    },
    content: {
      selectedBG: themes.colors.KBNight,
      deselectedBG: themes.colors.KBTwilightLight
    }
  };
  const indexOfCurrentTab = tabs.findIndex(value => value.type === currentTab);

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page('Results');
    }
  }, []);

  const scheduleAssessment = () => {
    if (window.analytics) {
      window.analytics.track('Appointment CTA Clicked');
    }

    history.push('/booking-quiz');
  };

  const selectCard = (index: number) => {
    const selectedTab = tabs[index];
    setCurrentTab(selectedTab.type);

    if (selectedTab.type === 'at-home') {
      if (window.analytics) {
        window.analytics.track('AHT Results Viewed - TRACK');
      }
    }
  };

  const getTabs = (data: ResponseData): Tab[] => {
    const allResults: Tab[] = [];
    if (!data) return [];

    const {
      results,
      dailyResults: maybeDailyResults,
      retrievalResult: maybeRetrievalResults
    } = data;

    const labResults = results.labs.concat(results.spermAnalyses).length > 0;
    const afcResults = results.afc.length > 0;
    if (labResults || afcResults) {
      allResults.push({
        type: TabTypes.FERTILITY,
        title: 'Assessment'
      });
    }

    const dailyResults: DailyResult[] = maybeDailyResults || [];
    if (dailyResults.length > 0) {
      // Don't render stimulation tab if there are no labs present on any of the days.
      // There are user's who have dailResults available but no labs yet, so no valuable data to show.
      const hasLabs = dailyResults.find((value: DailyResult) => {
        return value.labs.length > 0;
      });
      if (hasLabs) {
        allResults.push({
          type: TabTypes.STIMULATION,
          title: 'Stimulation'
        });
      }
    }

    const retrievalResult: RetrievalResult | null = maybeRetrievalResults;
    if (retrievalResult) {
      allResults.push({
        type: TabTypes.EGG_RETIEVAL,
        title: 'Egg Retrieval'
      });
    }

    const { homeTestingResults } = results;
    if (homeTestingResults.length) {
      allResults.push({
        type: TabTypes.AT_HOME,
        title: 'At-Home Test Results'
      });
    }

    return allResults;
  };

  const hasTab = (tabs: Tab[], tabName: TabTypes): boolean => {
    return !!tabs.find(val => val.type === tabName);
  };

  const hasQueryString = (queryString: string): boolean => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has(queryString);
  };

  const setupTabs = (data: ResponseData) => {
    if (currentTab !== null && !data) {
      return;
    }

    const availableTabs = getTabs(data);
    setTabs(availableTabs);

    const hasTabSelected = hasQueryString('activeTab');
    const hasAtHomeTab = hasTab(availableTabs, TabTypes.AT_HOME);
    const hasStimTab = hasTab(availableTabs, TabTypes.STIMULATION);
    const hasAssessmentTab = hasTab(availableTabs, TabTypes.FERTILITY);
    if (hasTabSelected && hasAtHomeTab) {
      setCurrentTab(TabTypes.AT_HOME);
    } else if (hasStimTab) {
      setCurrentTab(TabTypes.STIMULATION);
    } else if (hasAssessmentTab) {
      setCurrentTab(TabTypes.FERTILITY);
    }
  };

  const renderContent = (item: Tab, data?: ResponseData) => {
    if (!data) return null;
    if (!patient) return null;
    const { results, dailyResults, retrievalResult } = data;

    switch (item.type) {
      case TabTypes.FERTILITY:
        return (
          <Assessment
            results={results}
            onScheduleAssessment={scheduleAssessment}
            patient={patient}
          />
        );
      case TabTypes.STIMULATION:
        return <Stimulation dailyResults={dailyResults} />;
      case TabTypes.EGG_RETIEVAL:
        return <EggRetrieval retrievalResult={retrievalResult} />;
      case TabTypes.AT_HOME:
        return <AtHomeTesting patient={patient} results={results.homeTestingResults} />;
      default:
        return null;
    }
  };

  const hasResult = (data: ResponseData) => {
    const hasLabs = data?.results.labs.length;
    const hasAFC = data?.results.afc.length;
    const hasSpermAnalysis = data?.results.spermAnalyses.length;
    return hasLabs || hasAFC || hasSpermAnalysis;
  };

  const renderLoading = () => {
    return (
      <AbsoluteCentered>
        <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
      </AbsoluteCentered>
    );
  };

  return (
    <Query
      query={RESULTS_QUERY} // fetchPolicy required to ensure onCompleted calls every time
      fetchPolicy="cache-first"
      onCompleted={(data: ResponseData) => setupTabs(data)}
    >
      {({ data, loading, error }: ResultsQueryProps) => {
        if (error) {
          Honeybadger.notify('GraphqlQueryError', error);
          return null;
        }

        if (loading) {
          return renderLoading();
        }

        return (
          <ResultsContainer>
            <Title>Your Results</Title>

            <AtHomeWrapper>
              <RegisterCustomCode />
            </AtHomeWrapper>

            <ResultsCard>
              <ShadowWrapper>
                {hasResult(data) ? (
                  <TabNavigator
                    items={tabs}
                    currentIndex={indexOfCurrentTab}
                    getSectionTitle={(index: number, item: Tab) => item.title}
                    getUniqueKey={(index: number, item: Tab) => item.type}
                    renderContent={(index: number, item: Tab) => renderContent(item, data)}
                    onSelect={selectCard}
                    theme={tabNavigatorThemeConfig}
                  />
                ) : (
                  <EmptyState
                    canSchedule={!isViosPatient}
                    onScheduleAssessment={scheduleAssessment}
                  />
                )}
              </ShadowWrapper>
            </ResultsCard>
          </ResultsContainer>
        );
      }}
    </Query>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  patient: state.patient.patient
});

export const ResultsScreen = connect(mapStateToProps)(Results);
